import './index.css'
import '../react-select-v5-theme.css'

import cn from 'clsx'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import Select, { components as selectComponents } from 'react-select'
import type {
  DropdownIndicatorProps,
  MenuListProps,
  MultiValueProps,
  OptionProps,
  Props as ReactSelectProps,
} from 'react-select'
import AsyncSelect from 'react-select/async'
import AsyncCreatableSelect from 'react-select/async-creatable'
import CreatableSelect from 'react-select/creatable'
import InlineSVG from 'svg-inline-react'

import { tokenizeSearchText } from '@disco/utils/string-utils'

import type { DropdownOption } from 'types/ui'

import HighlightedText from 'components/ui/highlighted-text'
import Icon, { IconKey } from 'components/ui/icon'
import { matchesExistingOption } from 'components/ui/dropdowns/match-existing-option'
import Tag from 'components/ui/tag'

const defaultOptionValidation = (label?: string) =>
  !(!label || typeof label !== 'string' || label.trim() === '')

const createValidationFunction = (invalidNewOptions?: Array<string>) => {
  if (!invalidNewOptions || invalidNewOptions.length === 0) {
    return (label: string) => defaultOptionValidation(label)
  }
  return (label: string) =>
    defaultOptionValidation(label) && !invalidNewOptions.includes(label)
}

const asyncNoOptionsMessage: NonNullable<
  ReactSelectProps<DropdownOption, boolean>['noOptionsMessage']
> = ({ inputValue }) =>
  inputValue.trim() ? 'No results found' : 'Type to search'

type Props = {
  async?: boolean
  autoFocus?: boolean
  onBlur?: (...args: Array<any>) => any
  onChange: (...args: Array<any>) => any
  // TODO: fix this any type, check which type is correct in old version of react-select
  value: string | string[] | any
  name?: string
  options?: DropdownOption[]
  optionsIndexed?: (...args: Array<any>) => any
  promptTextCreator?: (...args: Array<any>) => any
  className?: string
  allowCreate?: boolean
  multi?: boolean
  multiSelectLimit?: number
  multiSelectMessage?: string
  clearable?: boolean
  placeholder?: string
  invalidNewOptions?: string[]
  disabled?: boolean
  simpleValue?: boolean
  loadOptions?: (...args: Array<any>) => any
  disabledRemoveTag?: boolean
  scrollContainer?: HTMLElement | null | undefined
  maxMenuHeight?: string
  cache?: boolean
  enableUp?: boolean
  arrowRendererIcon?: IconKey
  iconStrong?: boolean
  scrollToTop?: boolean
  truncateDisplayedOptions?: number
  shortMenu?: boolean
}

/**
 * A highly customizable dropdown component with support for async loading, creation, and multi-select.
 *
 * The public props map onto react-select v5 internally so consumers using the older
 * prop shape (callback-style `loadOptions`, `optionsIndexed`, `simpleValue`, etc.)
 * keep working.
 *
 * @param async - Whether to use async loading (defaults to false)
 * @param autoFocus - Whether to auto-focus the dropdown on mount
 * @param name - The name attribute for the dropdown
 * @param options - Array of dropdown options
 * @param value - The current value(s) of the dropdown
 * @param allowCreate - Whether to allow creating new options (defaults to false)
 * @param invalidNewOptions - Array of strings that are invalid as new options
 * @param multi - Whether to enable multi-select mode (defaults to false)
 * @param multiSelectLimit - Maximum number of items that can be selected (defaults to Infinity)
 * @param clearable - Whether the dropdown value can be cleared (defaults to false)
 * @param placeholder - Placeholder text for the dropdown
 * @param onBlur - Callback fired when the dropdown loses focus
 * @param onChange - Callback fired when the dropdown value changes
 * @param className - Additional CSS classes to apply
 * @param simpleValue - Whether to use simple string values instead of objects
 * @param loadOptions - Function to load options asynchronously
 * @param disabled - Whether the dropdown is disabled (defaults to false)
 * @param disabledRemoveTag - Whether removing tags is disabled in multi-select mode
 * @param scrollContainer - Container element the menu should escape (renders it fixed)
 * @param enableUp - Whether to show the dropdown menu upwards
 * @param arrowRendererIcon - Icon to use for the dropdown arrow
 * @param cache - Whether to cache async results (defaults to true)
 * @param iconStrong - Whether to use strong styling for the arrow icon
 */
const CustomDropdown = ({
  async,
  autoFocus,
  name,
  options,
  optionsIndexed,
  value,
  allowCreate,
  invalidNewOptions,
  multi,
  multiSelectLimit = Infinity,
  multiSelectMessage,
  clearable,
  placeholder,
  onBlur,
  onChange,
  promptTextCreator,
  className,
  simpleValue,
  loadOptions,
  disabled,
  disabledRemoveTag,
  scrollContainer,
  enableUp,
  shortMenu,
  truncateDisplayedOptions,
  arrowRendererIcon,
  cache,
  iconStrong,
  scrollToTop,
  maxMenuHeight,
}: Props) => {
  // Drives the optionsIndexed filter (see displayedOptions).
  const [inputValue, setInputValue] = useState('')
  const menuListElRef = useRef<HTMLElement | null>(null)

  const resolvedValue = useMemo(() => {
    const toOption = (val: any): DropdownOption | null => {
      if (val == null || val === '') return null
      if (typeof val === 'object') return val as DropdownOption
      const availableOptions = options ?? []
      const match =
        availableOptions.find(option => option.value === val) ??
        (typeof val === 'string' || typeof val === 'number'
          ? availableOptions.find(
              option => String(option.value) === String(val),
            )
          : undefined)
      return match ?? { value: val, label: String(val) }
    }

    const valueAsArray = (val: any): any[] => {
      if (val == null || val === '') return []
      if (Array.isArray(val)) return val
      if (simpleValue && typeof val === 'string') return val.split(',')
      return [val]
    }

    return multi
      ? valueAsArray(value)
          .map(toOption)
          .filter((o): o is DropdownOption => o != null)
      : toOption(value)
  }, [multi, options, simpleValue, value])

  const atLimit =
    !!multi &&
    Array.isArray(resolvedValue) &&
    resolvedValue.length >= multiSelectLimit

  // optionsIndexed is a caller-provided filter (options, query, exclude) => options;
  // run it here and disable v5's own filtering so the caller's result is authoritative.
  const displayedOptions = useMemo(() => {
    if (!optionsIndexed) return options
    return optionsIndexed(
      options ?? [],
      inputValue,
      Array.isArray(resolvedValue) ? resolvedValue : [],
    )
  }, [optionsIndexed, options, inputValue, resolvedValue])

  const handleChange = (newValue: any) => {
    if (!simpleValue) return onChange(newValue)
    if (multi)
      return onChange((newValue ?? []).map((o: DropdownOption) => o.value))
    return onChange(newValue ? newValue.value : '')
  }

  // Callers pass loadOptions as a callback, a synchronous array, or a promise;
  // wrap all three shapes into a promise for v5.
  const asyncLoadOptions = useCallback(
    (query: string) =>
      new Promise<DropdownOption[]>(resolve => {
        const toOptions = (data: any) =>
          Array.isArray(data) ? data : data?.options ?? []
        const maybe =
          loadOptions &&
          loadOptions(query, (_err: unknown, data: any) =>
            resolve(toOptions(data)),
          )
        if (maybe && typeof maybe.then === 'function') {
          maybe.then((d: any) => resolve(toOptions(d))).catch(() => resolve([]))
        } else if (maybe === null) {
          // Some callers (e.g. ArtistSelection when disabled) return null without
          // invoking the callback; resolve empty so the dropdown doesn't hang.
          resolve([])
        } else if (maybe !== undefined) {
          resolve(toOptions(maybe))
        }
        // maybe === undefined: the consumer used the callback, which resolves.
      }),
    [loadOptions],
  )

  const DropdownIndicator = useCallback(
    (props: DropdownIndicatorProps<DropdownOption, boolean>) => (
      // Cast: react-select's component types widen Option when isMulti is dynamic.
      <selectComponents.DropdownIndicator {...(props as any)}>
        {arrowRendererIcon ? (
          <Icon
            type={arrowRendererIcon}
            className={cn('inline', { arrowRendererIconStrong: iconStrong })}
            width={24}
            height={24}
          />
        ) : (
          <InlineSVG
            src={require('!svg-inline!assets/icons/arrow-down.svg')}
            width="24"
            height="24"
            raw
          />
        )}
      </selectComponents.DropdownIndicator>
    ),
    [arrowRendererIcon, iconStrong],
  )

  const MultiValue = useCallback(
    (props: MultiValueProps<DropdownOption, boolean>) => {
      const data = props.data as any
      const removable =
        !disabled && !disabledRemoveTag && data.clearableValue !== false
      return (
        <span className="Select-tag">
          <Tag
            tag={data.label}
            tagColor={data.color}
            onRemoveClick={
              removable
                ? () =>
                    props.removeProps.onClick?.({
                      preventDefault() {},
                      stopPropagation() {},
                    } as any)
                : undefined
            }
            icon={
              data.icon ? (
                <Icon
                  width={12}
                  height={12}
                  type={data.icon}
                  aria-label={data.label}
                />
              ) : null
            }
          />
        </span>
      )
    },
    [disabled, disabledRemoveTag],
  )

  const MenuList = useCallback(
    (props: MenuListProps<DropdownOption, boolean>) => {
      if (atLimit && multiSelectMessage) {
        return <div className="popover-item">{multiSelectMessage}</div>
      }
      const children =
        truncateDisplayedOptions && Array.isArray(props.children)
          ? props.children.slice(0, truncateDisplayedOptions)
          : props.children
      return (
        <selectComponents.MenuList
          // Same Option-widening cast as DropdownIndicator.
          {...(props as any)}
          innerRef={(el: HTMLElement | null) => {
            const forward = props.innerRef as any
            if (typeof forward === 'function') forward(el)
            else if (forward) forward.current = el
            // scrollToTop brings the selected option to the top of the menu on
            // fresh mount rather than jumping to row 0.
            if (scrollToTop && el && menuListElRef.current !== el) {
              menuListElRef.current = el
              requestAnimationFrame(() => {
                el.querySelector<HTMLElement>(
                  '.Select__option--is-selected',
                )?.scrollIntoView({ block: 'start' })
              })
            }
          }}
        >
          {children}
        </selectComponents.MenuList>
      )
    },
    [atLimit, multiSelectMessage, truncateDisplayedOptions, scrollToTop],
  )

  // Only meaningful when optionsIndexed is driving the filtering — that's
  // the only case where inputValue is tracked here rather than internally
  // by react-select.
  const searchTokens = useMemo(
    () => tokenizeSearchText(inputValue),
    [inputValue],
  )

  const Option = useCallback(
    (props: OptionProps<DropdownOption, boolean>) => (
      // Cast: same Option-widening issue as DropdownIndicator/MenuList.
      <selectComponents.Option {...(props as any)}>
        <HighlightedText text={props.data.label} highlights={searchTokens} />
      </selectComponents.Option>
    ),
    [searchTokens],
  )

  const components = useMemo(
    () => ({
      DropdownIndicator,
      IndicatorSeparator: () => null,
      MenuList,
      ...(multi ? { MultiValue } : {}),
      ...(optionsIndexed ? { Option } : {}),
    }),
    [DropdownIndicator, MenuList, MultiValue, multi, Option, optionsIndexed],
  )

  const commonProps = {
    classNamePrefix: 'Select',
    autoFocus,
    name,
    // Keep a `Select` class on the container so the shared stylesheet's
    // root-scoped rules apply (v5 doesn't add one).
    className: cn('Select', className),
    placeholder,
    isMulti: multi,
    isClearable: !!clearable,
    isDisabled: disabled,
    value: resolvedValue,
    onChange: handleChange,
    onBlur,
    // At the selection limit, stop offering more options.
    isOptionDisabled: () => atLimit,
    menuPlacement: enableUp ? 'top' : 'auto',
    // Fixed positioning lets the menu escape scrollable ancestors.
    menuPosition: scrollContainer ? 'fixed' : 'absolute',
    menuPortalTarget:
      scrollContainer && typeof document !== 'undefined'
        ? document.body
        : undefined,
    maxMenuHeight: shortMenu
      ? 144
      : maxMenuHeight
      ? parseInt(maxMenuHeight, 10)
      : undefined,
    // optionsIndexed already filtered the list; don't filter again.
    filterOption: optionsIndexed ? null : undefined,
    onInputChange: optionsIndexed
      ? (val: string, meta: { action: string }) => {
          if (meta.action === 'input-change') setInputValue(val)
          // v5 clears its own search on select/blur/menu-close; mirror that here
          // so optionsIndexed doesn't filter by a stale query on the next open.
          else if (
            meta.action === 'set-value' ||
            meta.action === 'input-blur' ||
            meta.action === 'menu-close'
          )
            setInputValue('')
        }
      : undefined,
    components,
  } satisfies Partial<ReactSelectProps<DropdownOption, boolean>>

  const creatableProps = allowCreate
    ? {
        formatCreateLabel: (input: string) =>
          promptTextCreator ? promptTextCreator(input) : `Create "${input}"`,
        isValidNewOption: (
          input: string,
          selectValue: readonly any[],
          selectOptions: readonly any[],
        ) =>
          createValidationFunction(invalidNewOptions)(input) &&
          !matchesExistingOption(input, selectValue, selectOptions),
      }
    : {}

  if (async) {
    // Show static options before any search; when there are none, pass `true`
    // so v5 calls loadOptions('') on mount — pure-async consumers (e.g.
    // artist-selection) rely on this initial autoload.
    const defaultOptions = displayedOptions?.length ? displayedOptions : true
    return allowCreate ? (
      <AsyncCreatableSelect
        {...commonProps}
        {...creatableProps}
        cacheOptions={cache !== false}
        defaultOptions={defaultOptions}
        loadOptions={asyncLoadOptions}
        noOptionsMessage={asyncNoOptionsMessage}
      />
    ) : (
      <AsyncSelect
        {...commonProps}
        cacheOptions={cache !== false}
        defaultOptions={defaultOptions}
        loadOptions={asyncLoadOptions}
        noOptionsMessage={asyncNoOptionsMessage}
      />
    )
  }

  return allowCreate ? (
    <CreatableSelect
      {...commonProps}
      {...creatableProps}
      options={displayedOptions}
    />
  ) : (
    <Select {...commonProps} options={displayedOptions} />
  )
}

export default CustomDropdown
